import { useRive, Layout, Fit, Alignment } from '@rive-app/react-canvas';
import { Turtle } from 'lucide-react';
import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import speedometer from '../assets/consulting_features/speedometer_ui.riv';
import xCard from '../assets/consulting_features/3xCard.webp';
import growthCard from '../assets/consulting_features/GrowthCard.webp';
import freeonlineaudit from '../assets/consulting_features/freeonlineaudit.webp';
import ProcessCard from '../assets/consulting_features/ProcessCard.webp';
import AIAuditCard from '../assets/consulting_features/AIAuditCard.webp';
import RoadmapCard from '../assets/consulting_features/RoadmapCard.webp';

const FeatureCard = memo(({ title, description, image }) => {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-[#0D0E12] p-6 h-full">
      <img 
        src={image} 
        alt="" 
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover opacity-30"
      />
      <div className="relative z-10">
        <h3 className="text-xl font-bold mb-2">{title}</h3>
        <p className="text-sm text-gray-300">{description}</p>
      </div>
    </div>
  );
});

const Consulting_Features = () => {
  const [shouldLoadRive, setShouldLoadRive] = useState(false);
  const [sectionInView, setSectionInView] = useState(false);
  const riveContainerRef = useRef(null);
  const sectionRef = useRef(null);

  // Intersection Observer for the entire section
  useEffect(() => {
    const sectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSectionInView(true);
          sectionObserver.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );

    if (sectionRef.current) {
      sectionObserver.observe(sectionRef.current);
    }

    return () => sectionObserver.disconnect();
  }, []);

  // Intersection Observer for lazy loading Rive animation
  useEffect(() => {
    const riveObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoadRive(true);
          riveObserver.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );

    if (riveContainerRef.current) {
      riveObserver.observe(riveContainerRef.current);
    }

    return () => riveObserver.disconnect();
  }, []);

  const riveConfig = shouldLoadRive ? {
    src: speedometer,
    autoplay: true,
    stateMachines: ['State Machine 1'],
    animations: [], // Removed specific animation name that was causing the error
    loop: true,
    layout: new Layout({
      fit: Fit.Contain,
      alignment: Alignment.Center,
    })
  } : null;

  const { RiveComponent: SpeedometerRive } = useRive(riveConfig);
  
  const features = [
    {
      title: "Prozess & Workflow Analysis",
      description: "Wir schauen uns deine Abläufe an, decken unnötige Routine- aufgaben auf und finden die Stellen, an denen KI wirklich Wirkung entfaltet.",
      image: ProcessCard
    },
    {
      title: "KI Audit & Report",
      description: "Du erhältst einen verständlichen, datenbasierten Report, der dir genau zeigt, wo du stehst und wo KI das größte Potential hat.",
      image: AIAuditCard
    },
    {
      title: "Was ist Ihr KI-Score?",
      description: "Finden Sie heraus, wo Ihr Unternehmen vom Einsatz intelligenter Lösungen besonders profitieren kann. [ Auf Karte klicken ]",
      image: freeonlineaudit
    },
    {
      title: "BLCKS KI Strategie",
      description: "Mit klarer KI-Strategie erreichst du Ergebnisse bis zu 3x schneller. Ohne endlose Experimente oder Fehlinvestitionen.",
      image: xCard
    },
    {
      title: "KI Roadmap Development",
      description: "Gemeinsam entwickeln wir eine klare Strategie, die zeigt, wie KI dir heute Zeit spart und morgen Wachstum bringt.",
      image: RoadmapCard
    },
    {
      title: "Wachstums & Umsatzpotenziale",
      description: "Wir denken über Kostensenkung hinaus und zeigen dir, wie du mit KI mehr Kunden gewinnst, Umsätze steigerst und nachhaltig wachsen kannst.",
      image: growthCard
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className="min-h-[200vh] text-white px-4 relative" 
      style={{
        background: 'linear-gradient(to bottom, #070709 0%, #010003 20%, #010003 100%)',
        opacity: sectionInView ? 1 : 0,
        transition: 'opacity 0.5s ease-in-out'
      }}
    >
      <div className="max-w-[960px] mx-auto py-10 sm:py-20 relative z-10 px-4 sm:px-0">
        <div className="relative w-full" ref={riveContainerRef}>
          {/* Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8">
            {/* Left Column */}
            <div className="space-y-4 sm:space-y-8 md:pt-[256px]">
              {features.slice(0, 3).map((feature, index) => (
                <div 
                  key={`left-${index}`}
                  className="rounded-2xl w-full h-[400px] sm:h-[512px] p-4 sm:p-6 flex flex-col justify-end relative group mx-auto max-w-[464px]"
                  onClick={() => {
                    if (feature.title === 'Was ist Ihr KI-Score?') {
                      window.open('https://audit.blcks.at', '_blank', 'noopener,noreferrer');
                    }
                  }}
                  style={{
                    backgroundImage: sectionInView ? `url(${feature.image})` : 'none',
                    backgroundColor: sectionInView ? 'transparent' : '#0D0E12',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    overflow: 'hidden',
                    cursor: feature.title === 'Was ist Ihr KI-Score?' ? 'pointer' : 'default',
                    transition: 'transform 0.3s ease-in-out, background-image 0.5s ease-in-out',
                    transform: 'translateZ(0)', // Force GPU acceleration
                    opacity: sectionInView ? 1 : 0.8,
                    transition: 'opacity 0.5s ease-in-out, transform 0.3s ease-in-out, background-image 0.5s ease-in-out'
                  }}
                >
                  {feature.title === 'Was ist Ihr KI-Score?' && (
                    <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                      <div className="absolute top-39 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] aspect-square max-w-[300px] sm:top-49.5 sm:w-[63%] sm:max-w-none">
                        <SpeedometerRive className="w-full h-full" />
                      </div>
                    </div>
                  )}
                  <p className="font-['Geist'] text-sm sm:text-base tracking-[0.00em]">
                    <span className="font-medium text-white">
                      {feature.title}{feature.title === 'Was ist Ihr KI-Score?' ? '' : '.'}
                    </span> {feature.description && (
                      <span className="font-light text-[#ADB0B9]"> {feature.description}</span>
                    )}
                  </p>
                </div>
              ))}
            </div>
            
            {/* Right Column */}
            <div className="space-y-4 sm:space-y-8">
              {features.slice(3, 6).map((feature, index) => (
                <div 
                  key={`right-${index}`}
                  className="rounded-2xl w-full h-[400px] sm:h-[512px] p-4 sm:p-6 flex flex-col justify-end mx-auto max-w-[464px]"
                  style={{
                    backgroundImage: sectionInView ? `url(${feature.image})` : 'none',
                    backgroundColor: sectionInView ? 'transparent' : '#0D0E12',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    transition: 'transform 0.3s ease-in-out, background-image 0.5s ease-in-out',
                    transform: 'translateZ(0)', // Force GPU acceleration
                    opacity: sectionInView ? 1 : 0.8,
                    transition: 'opacity 0.5s ease-in-out, transform 0.3s ease-in-out, background-image 0.5s ease-in-out'
                  }}
                >
                  <p className="font-['Geist'] text-sm sm:text-base tracking-[0.00em]">
                    <span className="font-medium text-white">
                      {feature.title}{feature.title === 'Was ist Ihr KI-Score?' ? '' : '.'}
                    </span> {feature.description && (
                      <span className="font-light text-[#ADB0B9]"> {feature.description}</span>
                    )}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default memo(Consulting_Features);
